import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug, 3) : [];
  const category = article ? categories.find((c) => c.slug === article.category) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-32">
          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '永葆青春',
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://lztprh.cn/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://lztprh.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://lztprh.cn/article/${article.slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
        />

        <article>
          <header className="py-10 md:py-16 bg-background-100">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {category?.name || '分类'}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
              </nav>

              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-400">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-user-line text-accent-600 text-sm"></i>
                  </div>
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </header>

          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto py-10 md:py-14">
            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-100 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{ra.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{ra.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}